/**
 * Proposal V2 options — shared client/server (Proposal Builder V2, S0;
 * `optionColumns` added in S1).
 *
 * Options ("Option A · 2K", "Option B · 4K") are ITEM-level: a quote line
 * carries `optionIds` naming the options it belongs to, and an empty or
 * missing list means "in every option". Scopes (areas) and their labour are
 * never option-filtered — labour is identical across options in V1.
 *
 * Pure and isomorphic by construction (no React, no Firebase, no Node, no
 * app imports): the customer viewer, the builder, the print view and the
 * server's snapshot/acceptance code all price an option through these
 * helpers, so "what is in Option B" has exactly one definition.
 */

export interface OptionTagged {
  optionIds?: string[];
}

/**
 * Is this item part of the given option?
 *
 * - no option selected (`optionId` undefined) → every item counts;
 * - an item with no tags (`optionIds` empty/undefined) → in every option;
 * - otherwise the item must name the option.
 */
export function itemInOption(item: OptionTagged, optionId: string | undefined): boolean {
  if (optionId === undefined) return true;
  const ids = item.optionIds;
  if (!Array.isArray(ids) || ids.length === 0) return true;
  return ids.includes(optionId);
}

/**
 * Return each scope with its items narrowed to the given option.
 *
 * Never drops a scope (an area with nothing left in an option still has its
 * labour and still renders), never touches labour or any other scope field.
 * Returns new scope objects; the input array and its scopes are not mutated.
 */
export function filterScopesForOption<S extends { items: OptionTagged[] }>(
  scopes: S[],
  optionId: string | undefined,
): S[] {
  return scopes.map((scope) => ({
    ...scope,
    items: scope.items.filter((item) => itemInOption(item, optionId)),
  }));
}

/**
 * The option a proposal opens on: the one marked recommended, else the
 * first, else undefined (a proposal with no options).
 */
export function defaultOptionId(
  v2: { options?: Array<{ id: string; recommended?: boolean }> } | undefined,
): string | undefined {
  const options = v2?.options;
  if (!Array.isArray(options) || options.length === 0) return undefined;
  return (options.find((o) => o.recommended) ?? options[0]).id;
}

/**
 * The option ids of a proposal, in display order — one Investment/
 * comparison column each. Empty when the proposal has no options (callers
 * then render a single column). Entries without an id are skipped.
 */
export function optionColumns(content: { options?: Array<{ id: string }> } | undefined): string[] {
  const options = content?.options;
  if (!Array.isArray(options)) return [];
  return options.filter((o) => o && typeof o.id === 'string' && o.id.length > 0).map((o) => o.id);
}

/**
 * The cheapest option — the "from $X" figure a multi-option proposal is
 * quoted at (office cards, customer emails; S7): the entry whose inc-tax
 * one-time total (`oneTimeTotal`) is the MINIMUM. Not the default option —
 * "from" means the lowest price on offer. Entries whose total is not a
 * finite number are skipped; ties keep the first in key order. Null when
 * nothing qualifies (no options, or no usable figures) — callers then fall
 * back to their single total.
 */
export function minOptionPricing<P extends { oneTimeTotal?: unknown }>(
  byOption: Record<string, P> | null | undefined,
): { optionId: string; pricing: P; oneTimeTotal: number } | null {
  if (!byOption || typeof byOption !== 'object') return null;
  let best: { optionId: string; pricing: P; oneTimeTotal: number } | null = null;
  for (const [optionId, pricing] of Object.entries(byOption)) {
    const total = pricing?.oneTimeTotal;
    if (typeof total !== 'number' || !Number.isFinite(total)) continue;
    if (!best || total < best.oneTimeTotal) best = { optionId, pricing, oneTimeTotal: total };
  }
  return best;
}

// ── Optional lines (R1 §3.5) ─────────────────────────────────────────────────

/** The fields the optional-line rule reads (live `PlaygroundQuoteItem` and frozen render items alike). */
export interface OptionalLine {
  id: string;
  isOptional?: boolean;
  optionalDefault?: boolean;
}

/**
 * Does this line start OUT of the total? An optional line the customer must
 * tick to add (`optionalDefault === false`). `optionalDefault` undefined
 * counts as included — the viewer's `lineTicked` and the server's
 * `isIncludedByDefault` (acceptance) read it the same way.
 */
export function isDefaultOffOptional(item: Omit<OptionalLine, 'id'>): boolean {
  return item.isOptional === true && item.optionalDefault === false;
}

/**
 * The ids of every line that starts out of the total — what the canvas, the
 * rail, the send freeze and the office's deal figures leave out (the
 * customer adds them with a tick; until then they are "+ $X if added").
 */
export function defaultOffOptionalIds(scopes: ReadonlyArray<{ items: ReadonlyArray<OptionalLine> }> | null | undefined): Set<string> {
  const out = new Set<string>();
  for (const scope of scopes ?? []) for (const item of scope.items ?? []) if (isDefaultOffOptional(item)) out.add(item.id);
  return out;
}

/**
 * Each scope with its default-off optional lines dropped (labour and every
 * other field untouched; the same scope object when it has none). For
 * pricing only — never persist the result (the H4 subtotal keeps ALL items).
 */
export function withoutDefaultOffLines<S extends { items: OptionalLine[] }>(scopes: S[]): S[] {
  return scopes.map((scope) => (scope.items.some(isDefaultOffOptional) ? { ...scope, items: scope.items.filter((i) => !isDefaultOffOptional(i)) } : scope));
}
